import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'

import { Text, ConfirmationResultModal } from '@percent/lemonade'
import { useMutation, APIErrorHandler, API_ERROR_MESSAGE } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import type { RemoveAccountModalProps } from './RemoveAccountModal.types'

export function RemoveAccountModal({ isOpen, onClose, onSuccess, account }: RemoveAccountModalProps) {
  const { t } = useTranslation()
  const { accountService } = useServices()
  const [errorMessage, setErrorMessage] = useState<string | undefined>()

  const [{ status, isPending }, { mutateAsync: removeAccount }] = useMutation(accountService.removeAccount)

  const state = status === 'success' ? 'success' : status === 'error' ? 'failure' : undefined

  const memberDisplay = account.fullName ? `${account.fullName} (${account.email})` : account.email

  const handleConfirm = async () => {
    setErrorMessage(undefined)
    try {
      await removeAccount(account)
      onSuccess?.()
    } catch (err) {
      const axiosError = err as AxiosError<{ error: Record<string, unknown> }>
      const apiError = APIErrorHandler(axiosError?.response?.data?.error)
      setErrorMessage(
        apiError === API_ERROR_MESSAGE.genericError
          ? t('account.modify.remove.failure.description', { member: memberDisplay })
          : apiError
      )
    }
  }

  const handleClose = () => {
    if (!isPending) {
      setErrorMessage(undefined)
      onClose()
    }
  }

  return (
    <ConfirmationResultModal
      success={{
        title: t('account.modify.remove.success'),
        description: t('account.modify.remove.success.description', { member: memberDisplay }),
        button: t('button.close')
      }}
      failure={{
        title: t('account.modify.remove.failure'),
        description: errorMessage ?? t('account.modify.remove.failure.description', { member: memberDisplay }),
        button: t('button.close')
      }}
      title={t('account.modify.remove.title')}
      primaryButtonText={t('account.modify.remove.confirmButton')}
      secondaryButtonText={t('button.cancel')}
      primaryBtnTestId="remove-account-submit-button"
      secondaryBtnTestId="remove-account-cancel-button"
      buttonTestId="remove-account-close-button"
      state={state}
      loading={isPending}
      isOpen={isOpen}
      onClose={handleClose}
      onConfirm={handleConfirm}
    >
      <Text>{t('account.modify.remove.description', { member: memberDisplay })}</Text>
    </ConfirmationResultModal>
  )
}
